<template>
    <UserTabs
        :selected-user="selectedUser"
        :personal-team="userPersonalTeam"
        tab-key="user-notifications"
    >
        <div class="mt-12">
            <inertia-link
                :href="
                    route('kiosk.users.notifications.create', {
                        user: selectedUser.id,
                    })
                "
            >
                <AtomicButton size="sm" color="yellow">
                    {{ t("vue.create") }}
                    <template #icon>
                        <PlusCircleIcon
                            class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </inertia-link>
        </div>

        <DataTable
            class="mt-10"
            :headers="headers"
            :items="notifications"
            searchable
        >
            <template #item-id="{ data }">
                <div class="flex max-w-[100px] truncate p-6 text-gray-500">
                    <div>
                        {{ data }}
                    </div>
                </div>
            </template>

            <template #item-created_at="{ data }">
                <div class="flex justify-center p-6">
                    <div v-if="data">
                        {{ format(new Date(data as string), "d. L. yyyy") }}
                    </div>
                </div>
            </template>

            <template #item-custom_show="{ row }">
                <inertia-link
                    :href="
                        route('kiosk.users.notifications.show', {
                            user: selectedUser.id as number,
                            notification: row.id as number,
                        })
                    "
                >
                    <AtomicButton size="sm" color="yellow">
                        <template #icon>
                            <BookOpenIcon
                                class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                            />
                        </template>
                    </AtomicButton>
                </inertia-link>
            </template>

            <template #item-custom_edit="{ row }">
                <inertia-link
                    :href="
                        route('kiosk.users.notifications.edit', {
                            notification: row.id as number,
                            user: selectedUser.id as number,
                        })
                    "
                >
                    <AtomicButton size="sm" color="white">
                        <template #icon>
                            <PencilSquareIcon
                                class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                            />
                        </template>
                    </AtomicButton>
                </inertia-link>
            </template>

            <template #item-custom_delete="{ row }">
                <AtomicButton
                    size="sm"
                    color="red"
                    @click="deleteNotification(row as IAnnouncement)"
                >
                    <template #icon>
                        <TrashIcon class="h-6 w-6" />
                    </template>
                </AtomicButton>
            </template>
        </DataTable>
    </UserTabs>
</template>
<script lang="ts" setup>
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IUser from "@/types/Members/IUser";
import { format } from "date-fns";
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
    BookOpenIcon,
} from "@heroicons/vue/24/outline";

import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import type IAnnouncement from "@/types/Announcements/IAnnouncement";
import route from "ziggy-js";
import useConfirm from "@/composables/useConfirm";
import { create } from "@/composables/useNotifications";
import { router } from "@inertiajs/vue3";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import { useTranslations } from "@/composables/useTranslations";

const { show } = useConfirm();

const props = defineProps<{
    selectedUser: IUser;
    notifications: IPaginationData<IAnnouncement>;
    userPersonalTeam: ICurrentTeam;
}>();

// Get access to translations
const { t } = useTranslations();

const headers: IDataTableHeader[] = [
    { text: t("vue.id"), id: "id", sortable: true, sortableId: "id" },
    { text: t("vue.type"), id: "type", sortable: true, sortableId: "type" },
    { text: t("vue.title"), id: "title", sortable: true, sortableId: "title" },
    {
        text: t("vue.created"),
        id: "created_at",
        align: "center",
        sortable: true,
        sortableId: "created_at",
    },
    { text: "", id: "custom_show" },
    { text: "", id: "custom_edit" },
    { text: "", id: "custom_delete" },
];

const deleteNotification = async (notification: IAnnouncement) => {
    const confirmed = await show({
        header: t("vue.delete_notification_header"),
        message: t("vue.delete_notification_message"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(
            route("kiosk.users.notifications.destroy", {
                notification: notification.id,
                user: props.selectedUser.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: t("vue.notification_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: t("vue.notification_not_deleted"),
                        type: "error",
                    });
                },
            },
        );
    }
};
</script>
